import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Firestore } from 'firebase/firestore';
import { SubscriptionPlanCat } from '@cmms/models';
import '../common/upgrade-placeholder/upgrade-placeholder.js';
import '../common/upgrade-modal/upgrade-modal.js';

// Children get registered via components/index.ts barrel
@customElement('technical-page')
export class TechnicalPage extends LitElement {
  static styles = css`
    :host { display:block; min-height: 100%; background: var(--sl-color-neutral-0); }
    .page-header { display:flex; justify-content:space-between; align-items:center; padding: 1rem 1.5rem 0; }
    .page-title { margin:0; font-size:1.25rem; font-weight:600; color: var(--sl-color-neutral-900); }
    .content { padding: 1rem 1.5rem; }
  `;

  @property({ type: String }) companyId: string = '';
  @property({ type: Object }) firestore: Firestore = null as any;
  @property({ type: String, attribute: 'initialtab' }) initialTab: 'templates' | 'audits' | '' = '';
  @property({ type: Number }) subscriptionPlan: SubscriptionPlanCat = SubscriptionPlanCat.STARTER;

  @state() private activeTab: 'templates' | 'audits' = 'templates';
  @state() private showUpgradeModal = false;
  @state() private upgradeContext = { action: '', feature: '' };

  connectedCallback(): void {
    super.connectedCallback();
    this.activeTab = this.resolveInitialTab();
  }

  private hasTechnicalAccess(): boolean {
    // Only Enterprise (3) has access, Starter (1) and Professional (2) do not
    return this.subscriptionPlan === SubscriptionPlanCat.ENTERPRISE;
  }

  private handleUpgradeRequest(event: CustomEvent) {
    this.upgradeContext = {
      action: event.detail.action || 'Access',
      feature: 'Technical Management'
    };
    this.showUpgradeModal = true;
  }

  private resolveInitialTab(): 'templates' | 'audits' {
    // 1) explicit prop
    if (this.initialTab === 'templates' || this.initialTab === 'audits') return this.initialTab;
    // 2) URL (hash or query)
    try {
      const hash = (window.location.hash || '').toLowerCase();
      if (hash.includes('audits')) return 'audits';
      if (hash.includes('templates')) return 'templates';
      const qp = new URLSearchParams(window.location.search);
      const tab = qp.get('tab');
      if (tab === 'audits' || tab === 'templates') return tab;
    } catch {}
    // 3) localStorage
    try {
      const stored = localStorage.getItem('cmms_technical_tab');
      if (stored === 'audits' || stored === 'templates') return stored;
    } catch {}
    return 'templates';
  }

  private handleTabChange(e: CustomEvent) {
    const id = (e.target as any)?.activeTab?.panel || '';
    // Panels ids: 'templates' and 'audits'
    if (id === 'templates' || id === 'audits') {
      this.activeTab = id;
      try {
        localStorage.setItem('cmms_technical_tab', id);
        const url = new URL(window.location.href);
        url.hash = `#technical:${id}`;
        history.replaceState({}, '', url.toString());
      } catch {}
    }
  }

  render() {
    // Show upgrade placeholder for Basic plan users
    if (!this.hasTechnicalAccess()) {
      return html`
        <div class="technical-page">
          <upgrade-placeholder
            featureName="Technical Management"
            description="Streamline your technical operations with advanced template management, audit workflows, and compliance tracking tools."
            icon="gear"
            .currentPlan=${this.subscriptionPlan}
            .benefits=${[
              'Custom technical templates and checklists',
              'Automated audit workflows and scheduling',
              'Compliance tracking and documentation',
              'Technical procedure standardization',
              'Quality control and validation processes',
              'Advanced reporting and analytics'
            ]}
            @upgrade-requested=${this.handleUpgradeRequest}
          ></upgrade-placeholder>

          <!-- Upgrade Modal -->
          <upgrade-modal
            ?open=${this.showUpgradeModal}
            featureName=${this.upgradeContext.feature}
            actionName=${this.upgradeContext.action}
            @upgrade-confirmed=${this.handleUpgradeRequest}
            @sl-request-close=${() => this.showUpgradeModal = false}
          ></upgrade-modal>
        </div>
      `;
    }

    return html`
      <div class="page-header">
        <h2 class="page-title">Technical</h2>
      </div>
      <div class="content">
        <sl-tab-group @sl-tab-show=${this.handleTabChange} placement="top">
          <sl-tab slot="nav" panel="templates" ?active=${this.activeTab === 'templates'}>Templates</sl-tab>
          <sl-tab slot="nav" panel="audits" ?active=${this.activeTab === 'audits'}>Audits</sl-tab>

          <sl-tab-panel name="templates" id="templates">
            <gmp-page embedded .companyId=${this.companyId} .firestore=${this.firestore}></gmp-page>
          </sl-tab-panel>
          <sl-tab-panel name="audits" id="audits">
            <gmp-audit-run-page .companyId=${this.companyId} .firestore=${this.firestore}></gmp-audit-run-page>
          </sl-tab-panel>
        </sl-tab-group>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'technical-page': TechnicalPage;
  }
}
